import { useState } from "react";
import { inviteMember } from "../../api/members";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { ProjectMember } from "../../types";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface InviteMemberDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called after a successful invite so the parent can refresh the member list.
  onInvited: (member: ProjectMember) => void;
}

export function InviteMemberDialog({
  projectId,
  open,
  onOpenChange,
  onInvited,
}: InviteMemberDialogProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  // showOwnerConfirm drives a secondary confirmation view rendered inside the
  // same dialog when the chosen role is "owner". Owners can delete the project
  // and remove all members, so we require explicit acknowledgement before sending.
  const [showOwnerConfirm, setShowOwnerConfirm] = useState(false);

  const resetForm = () => {
    setEmail("");
    setRole("member");
    setShowOwnerConfirm(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const doInvite = async () => {
    setLoading(true);
    try {
      const member = await inviteMember(projectId, email.trim(), role);
      notify.success("Invitation sent", {
        description: `${email} has been invited as ${role}.`,
      });
      onInvited(member);
      resetForm();
      onOpenChange(false);
    } catch (err: any) {
      // 409 Conflict means a pending invite already exists or they are already
      // a member — surface a specific message rather than a generic error.
      if (err?.response?.status === 409) {
        notify.error(err.response.data?.error || "Already invited or a member.");
      } else {
        handleError(err, { scope: "InviteMemberDialog", context: { projectId } });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    // Inviting as owner is a high-impact action — require a second confirmation
    // step within the same dialog before actually sending the invite.
    if (role === "owner" && !showOwnerConfirm) {
      setShowOwnerConfirm(true);
      return;
    }

    await doInvite();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        {showOwnerConfirm ? (
          // Owner confirmation view — rendered in place of the form to avoid
          // nested dialogs. The user must explicitly confirm before the API is called.
          <>
            <DialogHeader>
              <DialogTitle>Invite as Owner?</DialogTitle>
              <DialogDescription>
                You are about to grant full ownership of this project to{" "}
                <strong>{email}</strong>. Owners can delete the project, remove
                all members, and manage all settings.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowOwnerConfirm(false)}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                type="button"
                disabled={loading}
                onClick={doInvite}
              >
                {loading ? "Sending…" : "Yes, invite as Owner"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          // Normal invite form
          <>
            <DialogHeader>
              <DialogTitle>Invite collaborator</DialogTitle>
              <DialogDescription>
                Enter an email address and choose a role. Registered users get
                access immediately; others receive an invite link by email.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">
                      Owner — full control, can manage members
                    </SelectItem>
                    <SelectItem value="member">
                      Member — can deploy and edit services
                    </SelectItem>
                    <SelectItem value="viewer">
                      Viewer — read-only access
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                {/* Label changes to "Continue →" when owner is selected to signal
                    that a second confirmation step follows before the invite is sent. */}
                <Button type="submit" disabled={loading || !email.trim()}>
                  {loading
                    ? "Sending…"
                    : role === "owner"
                    ? "Continue →"
                    : "Send invite"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
